import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert
} from '@mui/material';
import { Groups as TeamsIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import unifiedApi from '../utils/unifiedApi';
import { TeamLogo, JerseyDisplay, TeamStatsChips, TeamRoster } from '../components/team';

const Teams = () => {
  const { leagueSlug, leagueId, leagueKey } = useParams();
  const key = leagueSlug ?? leagueId ?? leagueKey;
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get teams from new teams endpoint
        const teamsData = await unifiedApi.getLeagueTeams(key);
        
        if (!teamsData) {
          setError('League not found or not public');
          return;
        }

        setStandings(teamsData);
      } catch (err) {
        setError('Failed to load teams');
        console.error('Error loading teams:', err);
      } finally {
        setLoading(false);
      }
    };

    if (key) {
      loadTeams();
    }
  }, [key]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!standings || !standings.teams) {
    return (
      <Box mt={4}>
        <Alert severity="info">No teams data available</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <TeamsIcon color="primary" />
          {standings.leagueName} - Teams
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse all teams in the league
        </Typography>
      </Box>

      {/* Teams Grid */}
      <Grid container spacing={3}>
        {standings.teams
          .sort((a, b) => a.position - b.position)
          .map((team) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={team.teamId}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <CardContent sx={{ flex: 1, p: 3 }}>
                  {/* Team Logo - Full width, square - only show if logo exists */}
                  {(team.logoUrl || team.logo || team.logoPath) && (
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                      <TeamLogo 
                        team={team} 
                        size="100%" 
                        sx={{ 
                          aspectRatio: '1 / 1',
                          maxWidth: '250px',
                          width: '100%'
                        }} 
                      />
                    </Box>
                  )}
                  
                  {/* Team Name and Jersey */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography 
                      variant="h6" 
                      component={Link}
                      to={`/teams/${team.teamSlug}`}
                      className="team-card-header"
                      sx={{ 
                        textDecoration: 'none',
                        flex: 1,
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {team.teamName}
                    </Typography>
                    <Box sx={{ flexShrink: 0 }}>
                      <JerseyDisplay team={team} variant="jersey" size={24} />
                    </Box>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="h6" className="team-position-text">
                      Position: {team.position}
                    </Typography>
                    <Typography variant="body2" className="team-points-text">
                      {team.points} points
                    </Typography>
                  </Box>

                  <TeamStatsChips team={team} variant="public" sx={{ mb: 2 }} />

                  <TeamRoster team={team} variant="public" />
                  
                  {/* View Team Link */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                    <ViewIcon fontSize="small" color="primary" />
                    <Typography 
                      variant="body2" 
                      component={Link}
                      to={`/teams/${team.teamSlug}`}
                      sx={{ 
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': { 
                          textDecoration: 'underline' 
                        }
                      }}
                    >
                      VIEW TEAM
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
    </Box>
  );
};

export default Teams;